import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { Switch } from 'Src/components/Switch/Switch';
import { uploadImage } from 'Src/helpers/service/image';
import { AppUser } from 'Src/views/App/App';
import { HTMLEditorQuill } from 'Src/components/HTMLEditorQuill/HTMLEditorQuill';
import { getFileName } from '../../../../../../helpers/file/file';
import './TournamentCustomSectionForm.css';

interface Props {
  announcement: any;
  user: AppUser;
  onCloseClick: (index) => void;
  onGlobalAnnouncementsFormChange: (event, index, field) => void;
  announcementIndex: number;
}

interface State {
  isImageError: boolean;
  activeTab: 'details' | 'content';
}

export class TournamentCustomSectionForm extends Component<Props, State> {
  state: State = { isImageError: false, activeTab: 'details' };

  onImageSelected = event => {
    const image = event.target.files[0];
    if (!image) return;
    uploadImage(this.props.user, image)
      .then(response => {
        this.props.onGlobalAnnouncementsFormChange(
          `${window.apiImg}/images/${response.key}`,
          this.props.announcementIndex,
          'uploadImage'
        );
        this.setState({ isImageError: false });
      })
      .catch(() => this.setState({ isImageError: true }));
  };

  renderAttachments() {
    const files = this.props.announcement.attachments.files || [];
    return files.map((file, fileIndex) =>
      file.url ? (
        <div className="tcsForm__file" key={`attachment_${fileIndex}`}>
          <span>{getFileName(propz.get(file, ['name'], ''))}</span>
          <button
            type="button"
            onClick={() =>
              this.props.onGlobalAnnouncementsFormChange(fileIndex, this.props.announcementIndex, 'removeFileButton')
            }
          >
            ×
          </button>
        </div>
      ) : null
    );
  }

  render() {
    const { announcement, announcementIndex } = this.props;
    const { activeTab, isImageError } = this.state;
    const picUrl = propz.get(announcement, ['attachments', 'images', '0', 'url'], '');
    const date =
      announcement.date && !isNaN(new Date(announcement.date).getTime()) ? new Date(announcement.date) : null;
    const isDateVisible = propz.get(announcement, ['isDateVisible'], false);
    const title = propz.get(announcement, ['title'], '');
    const body = propz.get(announcement, ['body'], '');

    return (
      <div className="tcsForm">
        <nav className="tcsForm__tabs">
          <button
            type="button"
            className={activeTab === 'details' ? 'is-active' : ''}
            onClick={() => this.setState({ activeTab: 'details' })}
          >
            Details & media
          </button>
          <button
            type="button"
            className={activeTab === 'content' ? 'is-active' : ''}
            onClick={() => this.setState({ activeTab: 'content' })}
          >
            Content
          </button>
        </nav>

        {activeTab === 'details' ? (
          <div className="tcsForm__panel">
            <label className="tcsForm__label">Title</label>
            <input
              className="tcsForm__text"
              value={title}
              placeholder="Section title"
              onChange={event => this.props.onGlobalAnnouncementsFormChange(event, announcementIndex, 'title')}
            />

            <div className="tcsForm__dateRow">
              <div>
                <span className="tcsForm__label">Date and time</span>
                <SitDatePicker
                  mode="datetime"
                  centered
                  value={date}
                  format="DD MMM YYYY, HH:mm"
                  minuteStep={5}
                  clearable={false}
                  onChange={next =>
                    next instanceof Date && this.props.onGlobalAnnouncementsFormChange(next, announcementIndex, 'date')
                  }
                />
              </div>
              <div className="tcsForm__dateSwitch">
                <span>
                  <b>Show date and time</b>
                  <small>Display this timestamp above the section on the public site.</small>
                </span>
                <Switch
                  name={`showDate_${announcementIndex}`}
                  value={isDateVisible}
                  onChange={() =>
                    this.props.onGlobalAnnouncementsFormChange(
                      { target: { checked: !isDateVisible } },
                      announcementIndex,
                      'isDateVisible'
                    )
                  }
                />
              </div>
            </div>

            <div className="tcsForm__mediaGrid">
              <div>
                <span className="tcsForm__label">Attachments</span>
                <label className="tcsForm__upload">
                  <b>Choose a file</b>
                  <small>Add a downloadable document</small>
                  <input
                    type="file"
                    onChange={event =>
                      this.props.onGlobalAnnouncementsFormChange(event, announcementIndex, 'uploadFile')
                    }
                  />
                </label>
                {this.renderAttachments()}
              </div>
              <div>
                <span className="tcsForm__label">Photo</span>
                <label className="tcsForm__upload tcsForm__upload--photo">
                  {picUrl ? (
                    <img src={picUrl} alt="Section" />
                  ) : (
                    <>
                      <b>Choose a photo</b>
                      <small>Image shown with this section</small>
                    </>
                  )}
                  <input type="file" accept="image/*" onChange={this.onImageSelected} />
                </label>
                {picUrl ? (
                  <button
                    type="button"
                    className="tcsForm__removePhoto"
                    onClick={event =>
                      this.props.onGlobalAnnouncementsFormChange(event, announcementIndex, 'removeImageButton')
                    }
                  >
                    Remove photo
                  </button>
                ) : null}
                {isImageError ? <div className="invalid-feedback d-block">Error uploading image</div> : null}
              </div>
            </div>
          </div>
        ) : (
          <div className="tcsForm__panel tcsForm__panel--content">
            <p>Write the information that visitors should see in this custom section.</p>
            <HTMLEditorQuill
              user={this.props.user}
              onEmailBodyChange={text => this.props.onGlobalAnnouncementsFormChange(text, announcementIndex, 'body')}
              value={body}
              isEnabled
              placeholder="Write the section content here…"
            />
          </div>
        )}
      </div>
    );
  }
}
